import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, CheckCircle2, Users2, HandHeart, HandCoins, Award, Camera, Sparkles,
} from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { PageHero } from "@/pages/About";

const ZIMSHINE_SLUG = "zimshine-community-enterprise";
const ECOCASH_DISPLAY = "078 265 8473";

const OTHER_GROUPS = [
    "Pace Setters",
    "Winners",
    "Achievers",
    "Vendors",
    "Progress",
    "Sangodema Welders",
    "Sangodema Broilers",
    "Taguta",
    "Cross Border",
];

const SECTORS = [
    "Detergent manufacturing",
    "Broiler chicken production",
    "Clothing and garment production",
    "Welding and metal fabrication",
    "Footwear production",
    "Trading and cross-border enterprise",
];

const mediaUrl = (p) => (p?.startsWith("http") ? p : `${BACKEND_URL}${p}`);

export default function Zimshine() {
    const [project, setProject] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        api.get(`/public/projects/${ZIMSHINE_SLUG}`).then(({ data }) => setProject(data)).catch(() => {});
    }, []);

    const hero = project?.images?.[0] || `${BACKEND_URL}/api/media/auth-zimshine-products.jpg`;
    const gallery = project?.images || [
        "/api/media/auth-zimshine-products.jpg",
    ];

    return (
        <div data-testid="zimshine-story-page">
            {/* HERO */}
            <section className="relative bg-[color:var(--gsf-green-900)] text-white overflow-hidden" data-testid="zimshine-hero">
                <div className="absolute inset-0">
                    <img src={mediaUrl(hero)} alt="Members of the Zimshine community enterprise displaying Zimshine-branded detergent products at a public exhibition in Harare" className="w-full h-full object-cover opacity-45" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f14]/95 via-[#0f2b1d]/70 to-transparent" />
                </div>
                <div className="relative gsf-container py-20 sm:py-28">
                    <Link to="/impact-hub" className="text-xs text-white/70 hover:text-white flex items-center gap-1 mb-4" data-testid="zimshine-back-link">
                        <ArrowLeft size={12} /> Back to Impact Hub
                    </Link>
                    <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Featured Success Story — Zimshine</div>
                    <h1 className="mt-3 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-tight">
                        From Community Support to Sustainable Enterprise
                    </h1>
                    <div className="mt-5 flex flex-wrap gap-3 text-xs">
                        <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-400/40" data-testid="zimshine-verified-badge">
                            <CheckCircle2 size={12} /> Verified impact
                        </span>
                        <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20">Warren Park, Harare</span>
                        <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20">1 of 10 funded groups</span>
                    </div>
                </div>
            </section>

            {/* INTRO */}
            <section className="gsf-section" data-testid="zimshine-intro">
                <div className="gsf-container grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2 space-y-5 text-[color:var(--gsf-body)] leading-relaxed">
                        <p>
                            <strong>Zimshine</strong> is a Warren Park community enterprise supported by Global Seed
                            Foundation as part of a wider initiative that funded <strong>ten community enterprise
                            groups</strong>.
                        </p>
                        <p>
                            Through Foundation funding, skills development, mentorship and organised support, Zimshine
                            established a detergent-manufacturing operation serving its community and creating
                            livelihood opportunities for its members and their families.
                        </p>
                    </div>
                    <aside className="gsf-card p-6 space-y-3 text-sm" data-testid="zimshine-quick-facts">
                        <div className="gsf-eyebrow">At a glance</div>
                        <StatRow icon={HandHeart} label="Enterprise" value="Zimshine" />
                        <StatRow icon={Sparkles} label="Sector" value="Detergent Manufacturing" />
                        <StatRow icon={Award} label="Status" value="Active — invited to exhibitions" />
                        <StatRow icon={Users2} label="Programme" value="1 of 10 funded groups" />
                    </aside>
                </div>
            </section>

            {/* GROWTH & PROGRESS */}
            <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="zimshine-growth">
                <div className="gsf-container">
                    <div className="max-w-3xl">
                        <div className="gsf-eyebrow">Growth and progress</div>
                        <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">
                            Products manufactured. Exhibitions attended. Livelihoods strengthened.
                        </h2>
                    </div>
                    <div className="mt-8 grid md:grid-cols-3 gap-6">
                        <div className="gsf-card p-6" data-testid="zimshine-products-card">
                            <Sparkles className="text-[color:var(--gsf-green-700)]" size={22} strokeWidth={1.7} />
                            <h3 className="mt-3 font-serif text-lg text-[color:var(--gsf-green-700)]">Products manufactured</h3>
                            <p className="mt-2 text-sm text-[color:var(--gsf-body)] leading-relaxed">
                                Zimshine now manufactures a range of household detergents and cleaning products —
                                dishwashing liquid, thick bleach, foam bath, pine gel and sponges — under the
                                <em> Zimshine — Bright, Clean, Zimshine</em> brand.
                            </p>
                        </div>
                        <div className="gsf-card p-6" data-testid="zimshine-exhibition-card">
                            <Award className="text-[color:var(--gsf-green-700)]" size={22} strokeWidth={1.7} />
                            <h3 className="mt-3 font-serif text-lg text-[color:var(--gsf-green-700)]">Exhibition progress</h3>
                            <p className="mt-2 text-sm text-[color:var(--gsf-body)] leading-relaxed">
                                The enterprise has progressed from a community start-up to receiving invitations to
                                participate in exhibitions and public showcases across Harare.
                            </p>
                        </div>
                        <div className="gsf-card p-6" data-testid="zimshine-livelihoods-card">
                            <HandCoins className="text-[color:var(--gsf-green-700)]" size={22} strokeWidth={1.7} />
                            <h3 className="mt-3 font-serif text-lg text-[color:var(--gsf-green-700)]">Livelihoods created</h3>
                            <p className="mt-2 text-sm text-[color:var(--gsf-body)] leading-relaxed">
                                Zimshine's journey demonstrates how practical funding, mentorship and market exposure
                                can help vulnerable communities develop sustainable income-generating enterprises.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* WIDER PROGRAMME CONTEXT — 10 groups */}
            <section className="gsf-section" data-testid="zimshine-programme-context">
                <div className="gsf-container">
                    <div className="max-w-3xl">
                        <div className="gsf-eyebrow">Wider programme context</div>
                        <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">
                            Zimshine is one of ten funded community enterprise groups.
                        </h2>
                        <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                            Zimshine was funded alongside nine other community enterprise groups. Together the ten
                            groups operate across a range of livelihood sectors, chosen to match local skills, markets
                            and community-identified opportunities.
                        </p>
                    </div>

                    <div className="mt-10 grid md:grid-cols-2 gap-6">
                        <div className="gsf-card p-6">
                            <div className="gsf-eyebrow">The other nine funded groups</div>
                            <ul className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm text-[color:var(--gsf-body)]" data-testid="zimshine-other-groups">
                                {OTHER_GROUPS.map((g) => (
                                    <li key={g} className="flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[color:var(--gsf-green-600)]" /> {g}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="gsf-card p-6">
                            <div className="gsf-eyebrow">Enterprise sectors</div>
                            <ul className="mt-3 grid grid-cols-1 gap-y-1.5 text-sm text-[color:var(--gsf-body)]" data-testid="zimshine-sectors">
                                {SECTORS.map((s) => (
                                    <li key={s} className="flex items-center gap-1.5"><Sparkles size={12} className="text-[color:var(--gsf-green-600)]" /> {s}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMMUNITY REACH — carefully worded */}
            <section className="bg-[color:var(--gsf-green-900)] text-white gsf-section" data-testid="zimshine-reach">
                <div className="gsf-container grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Community reach</div>
                        <h2 className="mt-3 font-serif text-3xl font-bold">Livelihoods supported across ten groups.</h2>
                    </div>
                    <div className="md:col-span-7 space-y-4 text-white/85 leading-relaxed">
                        <p>
                            <strong>Each funded group supports the livelihoods of at least 75 family members</strong>{" "}
                            through enterprise participation, household income and wider community benefit.
                        </p>
                        <p>
                            <strong>Collectively, the ten enterprise groups support the livelihoods of at least
                            750 family members.</strong>
                        </p>
                        <p className="text-white/60 text-xs">
                            This figure is not combined with other beneficiary statistics unless the underlying records
                            confirm there is no overlap.
                        </p>
                    </div>
                </div>
            </section>

            {/* PHOTO GALLERY */}
            {gallery.length > 0 && (
                <section className="gsf-section" data-testid="zimshine-gallery-section">
                    <div className="gsf-container">
                        <div className="gsf-eyebrow">Photo gallery</div>
                        <h2 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Zimshine in pictures</h2>
                        <figcaption className="mt-2 text-xs text-[color:var(--gsf-body)] italic max-w-2xl">
                            Zimshine detergent products displayed at an exhibition in Harare by members of the Global
                            Seed Foundation-supported community enterprise.
                        </figcaption>
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="zimshine-gallery">
                            {gallery.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setLightbox(mediaUrl(img))}
                                    className="aspect-square overflow-hidden rounded-xl group focus:outline-none focus:ring-4 focus:ring-[color:var(--gsf-gold-500)]/40"
                                    data-testid={`zimshine-gallery-${i}`}
                                >
                                    <img src={mediaUrl(img)} alt={`Zimshine — ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CALLS TO ACTION */}
            <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="zimshine-cta">
                <div className="gsf-container text-center max-w-3xl mx-auto">
                    <div className="gsf-eyebrow">Support the next community enterprise</div>
                    <h2 className="mt-3 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">
                        Help another group grow.
                    </h2>
                    <p className="mt-4 text-[color:var(--gsf-body)] leading-relaxed">
                        Zimshine's success shows what community-led enterprise can achieve. Your support helps the
                        next group of vulnerable community members build a sustainable enterprise of their own.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link to="/enterprise-portfolio" className="gsf-btn-primary text-sm" data-testid="zimshine-cta-portfolio">
                            Support Community Enterprises <ArrowRight size={14} />
                        </Link>
                        <Link to="/donate" className="gsf-btn-gold text-sm" data-testid="zimshine-cta-ecocash">
                            Donate via EcoCash · {ECOCASH_DISPLAY}
                        </Link>
                        <Link to="/get-involved#partner" className="gsf-btn-outline text-sm" data-testid="zimshine-cta-partner">
                            Partner With Global Seed Foundation
                        </Link>
                        <Link to="/impact-hub" className="gsf-btn-ghost text-sm" data-testid="zimshine-cta-back">
                            Back to Impact Hub
                        </Link>
                    </div>
                </div>
            </section>

            {/* LIGHTBOX */}
            {lightbox && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
                    onClick={() => setLightbox(null)}
                    data-testid="zimshine-lightbox"
                >
                    <figure className="max-w-6xl w-full">
                        <img src={lightbox} alt="Zimshine" className="w-full max-h-[80vh] object-contain" />
                        <figcaption className="mt-4 text-white/85 text-sm text-center italic">
                            <Camera size={12} className="inline mr-1" />
                            Authentic Global Seed Foundation photograph.
                        </figcaption>
                    </figure>
                </div>
            )}
        </div>
    );
}

function StatRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <Icon size={16} className="text-[color:var(--gsf-green-700)]" />
            <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-400">{label}</div>
                <div className="font-medium">{value}</div>
            </div>
        </div>
    );
}
